import { useEffect, useState } from "react";
import { Button } from "@chakra-ui/react";

export const META_IDENTITY_CATALOG_REFRESH_LABEL = "Refresh from Meta";

const REFRESH_COOLDOWN_MS = 60_000;

type MetaIdentityCatalogRefreshButtonProps = {
  onRefresh: () => void | Promise<void>;
  isRefreshing?: boolean;
  width?: string;
};

/** Triggers force_sync catalog refresh (emit + poll); use under identity pickers. */
export function MetaIdentityCatalogRefreshButton({
  onRefresh,
  isRefreshing = false,
  width = "100%",
}: MetaIdentityCatalogRefreshButtonProps) {
  const [cooldownUntilMs, setCooldownUntilMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!cooldownUntilMs || cooldownUntilMs <= Date.now()) {
      return;
    }
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1_000);
    return () => {
      window.clearInterval(timer);
    };
  }, [cooldownUntilMs]);

  const cooldownRemainingSec =
    cooldownUntilMs && cooldownUntilMs > nowMs
      ? Math.ceil((cooldownUntilMs - nowMs) / 1_000)
      : 0;
  const isCoolingDown = cooldownRemainingSec > 0;
  const isDisabled = isRefreshing || isCoolingDown;

  return (
    <Button
      size="sm"
      variant="outline"
      width={width}
      colorScheme="gray"
      fontWeight="medium"
      isLoading={isRefreshing}
      loadingText="Syncing from Meta…"
      isDisabled={isDisabled}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isDisabled) {
          return;
        }
        setCooldownUntilMs(Date.now() + REFRESH_COOLDOWN_MS);
        void onRefresh();
      }}
    >
      {isCoolingDown
        ? `Refresh available in ${cooldownRemainingSec}s`
        : META_IDENTITY_CATALOG_REFRESH_LABEL}
    </Button>
  );
}
